import { useState, useEffect, useCallback } from 'react';

interface UseResponsiveHeightConfig {
  headerHeight?: number;
  searchBarHeight?: number;
  padding?: number;
  minHeight?: number;
  footerHeight?: number;
  extraOffsets?: number[];
}

interface UseResponsiveHeightReturn {
  height: number;
  recalculateHeight: () => void;
}

export const LAYOUT_PRESETS = {
  extensionList: {
    headerHeight: 60,
    searchBarHeight: 60,
    padding: 40,
    minHeight: 300
  },
  callList: {
    headerHeight: 60,
    padding: 20,
    minHeight: 400
  },
  modal: {
    padding: 40,
    minHeight: 200
  },
  sidebar: {
    headerHeight: 80,
    footerHeight: 60,
    padding: 20,
    minHeight: 500
  },
  fullViewport: {
    minHeight: 100
  }
} as const;

export const useResponsiveHeight = (config: UseResponsiveHeightConfig = {}): UseResponsiveHeightReturn => {
  const {
    headerHeight = 0,
    searchBarHeight = 0,
    padding = 0,
    minHeight = 300,
    footerHeight = 0,
    extraOffsets = []
  } = config;

  const [height, setHeight] = useState(minHeight);

  const calculateHeight = useCallback(() => {
    const totalOffsets = headerHeight + searchBarHeight + padding + footerHeight + 
                        extraOffsets.reduce((sum, offset) => sum + offset, 0);
    const availableHeight = window.innerHeight - totalOffsets;
    const calculatedHeight = Math.max(minHeight, availableHeight);
    setHeight(calculatedHeight);
    return calculatedHeight;
  }, [headerHeight, searchBarHeight, padding, minHeight, footerHeight, extraOffsets]);

  useEffect(() => {
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, [calculateHeight]);

  return { height, recalculateHeight: calculateHeight };
};

export const useResponsiveHeightPreset = (
  preset: keyof typeof LAYOUT_PRESETS,
  overrides: Partial<UseResponsiveHeightConfig> = {}
): UseResponsiveHeightReturn => {
  const config = { ...LAYOUT_PRESETS[preset], ...overrides };
  return useResponsiveHeight(config);
};

export default useResponsiveHeight;
